import React, { useState, useMemo } from 'react';
import { ListFilter, Plus, Search, Trash2, ChevronRight, X } from 'lucide-react';
import { safeAsync, emptyMsg, btnPrimary, btnSm, cardWrap, neuShadow, neuShadowSm, inp } from './crmShared';

export default function FiltersTab({ savedFilters, canCreate, loadExtended }) {
  const [expandedGroups, setExpandedGroups] = useState(new Set(['shared']));
  const [searchTerm, setSearchTerm] = useState('');

  // Group filters by is_shared
  const grouped = useMemo(() => {
    const groups = { shared: [], personal: [] };
    savedFilters.forEach(f => {
      if (f.is_shared) {
        groups.shared.push(f);
      } else {
        groups.personal.push(f);
      }
    });
    return groups;
  }, [savedFilters]);

  // Filter by search term
  const filteredGroups = useMemo(() => {
    if (!searchTerm) return grouped;
    const term = searchTerm.toLowerCase();
    return {
      shared: grouped.shared.filter(f =>
        f.name.toLowerCase().includes(term) ||
        Object.entries(typeof f.filter_config === 'object' ? f.filter_config : {})
          .some(([k, v]) => k.toLowerCase().includes(term) || String(v).toLowerCase().includes(term))
      ),
      personal: grouped.personal.filter(f =>
        f.name.toLowerCase().includes(term) ||
        Object.entries(typeof f.filter_config === 'object' ? f.filter_config : {})
          .some(([k, v]) => k.toLowerCase().includes(term) || String(v).toLowerCase().includes(term))
      ),
    };
  }, [grouped, searchTerm]);

  // Auto-expand on search
  React.useEffect(() => {
    if (searchTerm) {
      setExpandedGroups(new Set(['shared', 'personal']));
    }
  }, [searchTerm]);

  const toggleGroup = (groupId) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  const expandAll = () => {
    setExpandedGroups(new Set(['shared', 'personal']));
  };

  const collapseAll = () => {
    setExpandedGroups(new Set());
  };

  const groupConfigs = [
    { id: 'shared', label: 'Shared Filters', color: '#2E8B98' },
    { id: 'personal', label: 'Personal Filters', color: '#F26522' },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#212529', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <ListFilter size={18} color="#256F7A" /> Smart Lists & Saved Filters
        </h3>
        {canCreate && (
          <button onClick={safeAsync(async () => {
            const name = prompt('Filter name:');
            if (!name) return;
            const stage = prompt('Pipeline stage (leave blank for all):') || '';
            const type = prompt('Contact type (leave blank for all):') || '';
            const grade = prompt('Lead grade (A+, A, B, C, D, F, or blank):') || '';
            const noDays = prompt('No activity in X days (or blank):') || '';
            const config = {};
            if (stage) config.pipeline_stage = stage;
            if (type) config.contact_type = type;
            if (grade) config.grade = grade;
            if (noDays) config.no_activity_days = parseInt(noDays);
            await window.zignagenixAPI.createSavedFilter({ name, filter_config: config, is_shared: true });
            loadExtended();
          })} style={btnPrimary}><Plus size={13} /> New Smart List</button>
        )}
      </div>

      {/* Quick filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          { label: 'Hot Leads (A+/A)', config: { grade: 'A' }, color: '#62BB46' },
          { label: 'Stale (7d inactive)', config: { no_activity_days: 7 }, color: '#F26522' },
          { label: 'New this month', config: { created_after: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString() }, color: '#2E8B98' },
          { label: 'Prospects only', config: { contact_type: 'prospect' }, color: '#256F7A' },
        ].map((qf, i) => (
          <button key={i} onClick={safeAsync(async () => {
            const results = await window.zignagenixAPI.applySavedFilter(qf.config);
            alert(`${qf.label}: ${results.length} contacts match`);
          })} style={{ ...btnSm, borderLeft: `3px solid ${qf.color}` }}>{qf.label}</button>
        ))}
      </div>

      {/* Search bar */}
      <div style={{ marginBottom: 16, position: 'relative' }}>
        <input
          type="text"
          placeholder="Search filters..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            ...inp,
            paddingRight: 32,
            width: '100%',
            boxSizing: 'border-box',
          }}
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm('')}
            style={{
              position: 'absolute',
              right: 10,
              top: 10,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#6C757D',
            }}
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Expand/Collapse All buttons */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button onClick={expandAll} style={btnSm}>Expand All</button>
        <button onClick={collapseAll} style={btnSm}>Collapse All</button>
      </div>

      {/* Accordion groups */}
      {savedFilters.length === 0 ? (
        <div style={emptyMsg}>No saved filters. Create a smart list to quickly segment contacts.</div>
      ) : (
        groupConfigs.map(groupConfig => {
          const filters = filteredGroups[groupConfig.id];
          if (filters.length === 0) return null;
          const isExpanded = expandedGroups.has(groupConfig.id);

          return (
            <div key={groupConfig.id} style={{ marginBottom: 12 }}>
              {/* Group header */}
              <button
                onClick={() => toggleGroup(groupConfig.id)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 16px',
                  background: '#F8F9FA',
                  border: `1px solid #E9ECEF`,
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#212529',
                  transition: 'background-color 0.2s',
                  boxShadow: neuShadowSm,
                }}
                onMouseEnter={(e) => e.target.style.background = '#F1F3F5'}
                onMouseLeave={(e) => e.target.style.background = '#F8F9FA'}
              >
                <ChevronRight
                  size={16}
                  style={{
                    color: groupConfig.color,
                    transition: 'transform 0.3s',
                    transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                  }}
                />
                <div style={{ flex: 1, textAlign: 'left' }}>
                  <span style={{ color: groupConfig.color, marginRight: 4 }}>●</span>
                  {groupConfig.label}
                </div>
                <div
                  style={{
                    background: groupConfig.color,
                    color: 'white',
                    fontSize: 11,
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: 12,
                    minWidth: 24,
                    textAlign: 'center',
                  }}
                >
                  {filters.length}
                </div>
              </button>

              {/* Expandable content */}
              <div
                style={{
                  maxHeight: isExpanded ? '2000px' : '0px',
                  overflow: 'hidden',
                  transition: 'max-height 0.3s ease-in-out',
                }}
              >
                <div
                  style={{
                    background: '#FFFFFF',
                    border: `1px solid #E9ECEF`,
                    borderTop: 'none',
                    borderRadius: '0 0 8px 8px',
                    boxShadow: neuShadow,
                  }}
                >
                  {filters.map((f, i) => {
                    const fc = typeof f.filter_config === 'object' ? f.filter_config : {};
                    return (
                      <div
                        key={f.id}
                        style={{
                          padding: '12px 16px',
                          borderBottom: i < filters.length - 1 ? '1px solid #E9ECEF' : 'none',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          background: i % 2 === 1 ? '#F8F9FA' : '#FFFFFF',
                        }}
                      >
                        <ListFilter size={14} color="#256F7A" />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#212529' }}>{f.name}</div>
                          <div style={{ fontSize: 10, color: '#6C757D' }}>
                            {Object.entries(fc).map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`).join(' · ') || 'All contacts'}
                            {f.is_shared && ' · Shared'}
                          </div>
                        </div>
                        <button onClick={safeAsync(async () => {
                          const results = await window.zignagenixAPI.applySavedFilter(fc);
                          alert(`"${f.name}" matched ${results.length} contacts`);
                        })} style={btnSm}><Search size={11} /> Run</button>
                        <button onClick={safeAsync(async () => { await window.zignagenixAPI.deleteSavedFilter(f.id); loadExtended(); })}
                          style={{ ...btnSm, color: '#DC3545' }}><Trash2 size={11} /></button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
